import { FC } from 'react';

import { Image } from 'expo-image';
import { StyleSheet } from 'react-native';

import { Box, Typography } from 'components/ui';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { getDesignToken, primitive, spacing } from 'styles/constants';

import { BlockContainer } from '../styled';
import { WalmartLandingOffersProps } from '../types';
import { useLoyaltyWalmart } from '../use-loyalty-walmart';

import { WalmartWeekOffers } from './walmart-week-offers';

const walmartPlusLogo = require('../walmart-plus-logo.svg');

const save25Image = require('./25-off.png');
const freeItemImage = require('./free-item.png');
const membershipImageAsset = require('./w-membership.png');

export const WalmartLandingOffers: FC<WalmartLandingOffersProps> = ({ isAlreadyLinked }) => {
  const { data, loading } = useLoyaltyWalmart();
  const showSeasonalPromo = useFlag(LaunchDarklyFlag.SHOW_WALMART_SEASONAL_PROMO);
  const isMobile = useIsMobileBreakpoint();
  if (loading) {
    return null;
  }

  const logo = data?.logo?.locale;

  return (
    <BlockContainer>
      <Typography
        variant="headerThree"
        fontSize={isMobile ? 'lg' : '2xl'}
        textAlign="center"
        style={[styles.sectionTitle, isMobile && styles.mobileSectionTitle]}
      >
        {t('walmartLandingExclusiveBenefits', 'Exclusive Benefits')}
      </Typography>
      {showSeasonalPromo && data?.seasonalPromoConfig && (
        <WalmartWeekOffers {...data.seasonalPromoConfig} walmartBadge={logo} />
      )}
      <Box style={[styles.cards, isMobile && styles.mobileCards]}>
        <Box style={[styles.card, isMobile && styles.mobileCard]}>
          <Image source={save25Image} contentFit="contain" style={styles.cardIcon} />
          <Typography variant="headerThree" fontSize="2xl" bold style={styles.title}>
            {t('walmartLandingSave25', {
              defaultValue: 'Save 25% on your BK® {separator}favorites',
              separator: isAlreadyLinked && !isMobile ? '' : '\n',
            })}
          </Typography>
          <Typography
            fontSize="md"
            color={getDesignToken('text-default')}
            style={styles.description}
          >
            {t('walmartLandingDailyOnV2', 'Once every day.')}
          </Typography>
        </Box>
        <Box style={[styles.card, isMobile && styles.mobileCard]}>
          <Image source={freeItemImage} contentFit="contain" style={styles.cardIcon} />
          <Typography variant="headerThree" fontSize="2xl" bold style={styles.title}>
            {t('walmartLandingFreeMenuItem', {
              defaultValue: 'Choice of a FREE BK® {separator}menu item',
              separator: isAlreadyLinked && !isMobile ? '' : '\n',
            })}
          </Typography>
          <Typography
            fontSize="md"
            color={getDesignToken('text-default')}
            style={styles.description}
          >
            {t('walmartLandingWithPurchaseQuarterly', 'With a purchase every quarter.')}
          </Typography>
          <Typography style={styles.finePrint} fontSize="2xs">
            {t('walmartLandingAvailableChoices', {
              defaultValue:
                "Available choices: Whopper®, Original {mobileSeparator}Chicken Sandwich, Double {desktopSeparator}Cheeseburger, {mobileSeparator}Cheeseburger, or Croissan'wich.",
              mobileSeparator: isAlreadyLinked && !isMobile ? '' : '\n',
              desktopSeparator: isAlreadyLinked && !isMobile ? '\n' : '',
            })}
          </Typography>
        </Box>
        {!isAlreadyLinked && (
          <Box style={[styles.card, isMobile && styles.mobileCard]}>
            <Box style={styles.membershipBadge}>
              <Typography color="#FFF" fontSize="md" bold>
                {t('walmartLanding40Off', '40% OFF')}
              </Typography>
            </Box>
            <Image
              source={membershipImageAsset}
              contentFit="contain"
              style={[styles.cardIcon, { marginBottom: spacing.$4_5 }]}
            />
            <Typography fontSize="md" style={styles.oldPrice}>
              {t('walmartMembershipOldPrice', '$12.95')}
            </Typography>
            <Typography variant="headerThree" fontSize="2xl" bold style={styles.title}>
              {t('walmartLandingWalmartMembership', '$7.77/month \nfor Walmart+')}
            </Typography>
            <Typography
              fontSize="md"
              color={getDesignToken('text-default')}
              style={styles.description}
            >
              {t('walmartLandingAnnualMembership', 'Annual membership.')}
            </Typography>
            <Image
              source={walmartPlusLogo}
              style={styles.walmartPlusLogo}
              contentFit="contain"
              alt="Walmart+"
            />
          </Box>
        )}
      </Box>
    </BlockContainer>
  );
};

const styles = StyleSheet.create({
  sectionTitle: { marginBottom: spacing.$10, marginTop: spacing.$10 },
  mobileSectionTitle: { marginBottom: spacing.$6, marginTop: spacing.$6 },
  description: { textAlign: 'center' },
  cards: { flexDirection: 'row', flexWrap: 'nowrap', gap: spacing.$6 },
  card: {
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: primitive.$white,
    borderRadius: spacing.$3,
    justifyContent: 'flex-start',
    paddingHorizontal: spacing.$5,
    paddingVertical: spacing.$6,
    flex: 1,
  },
  mobileCard: {
    flexBasis: 'auto',
  },
  mobileCards: {
    flexDirection: 'column',
  },
  cardIcon: { height: 72, marginBottom: spacing.$6, width: 72 },
  title: {
    color: getDesignToken('text-default'),
    marginBottom: spacing.$2_5,
    textAlign: 'center',
    lineHeight: 28.8,
  },
  membershipBadge: {
    backgroundColor: '#D62300',
    borderRadius: spacing.$4,
    left: 14,
    paddingHorizontal: spacing.$1_5,
    paddingVertical: 3,
    position: 'absolute',
    top: 14,
  },
  membershipPlaceholder: { height: 80 },
  finePrint: {
    color: '#5F5F5F',
    lineHeight: 12.5,
    textAlign: 'center',
    marginTop: spacing.$2_5,
  },
  walmartPlusLogo: { width: '100%', height: 24, marginTop: spacing.$3 },
  oldPrice: {
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.$blackOpacity50,
    textDecorationLine: 'line-through',
  },
});
